import React, { useState, useEffect, useRef } from 'react';
import financialService from '../../services/financialService';
import FinancialControls from './FinancialControls';
import DimensionAnalysis from './DimensionAnalysis';
import TopUtilityCharts from './TopUtilityCharts';
import DistributionCharts from './DistributionCharts';
import ComparativeCharts from './ComparativeCharts';
import DetailedDataTable from './DetailedDataTable';
import PriceExploration from './PriceExploration';
import './FinancialDashboard.css';

const FinancialDashboard = ({ marketplace }) => {
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [cacheKey, setCacheKey] = useState(null);
  const [baseCacheKey, setBaseCacheKey] = useState(null); // Cache key original sin modificaciones de precio
  const [stats, setStats] = useState(null);
  const [metricas, setMetricas] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dimension');
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Configuración de análisis
  const [horizonteTemporal, setHorizonteTemporal] = useState('Mensual');
  const [tipoVolumen, setTipoVolumen] = useState('Predicciones');
  const [fuentePrecio, setFuentePrecio] = useState('Publications (actual)');
  
  // Usar refs para trackear última configuración procesada (evitar loops)
  const lastConfigRef = useRef({
    horizonteTemporal: 'Mensual',
    tipoVolumen: 'Predicciones',
    fuentePrecio: 'Publications (actual)',
    cacheKey: null
  });
  
  // AbortController para cancelar peticiones pendientes
  const abortControllerRef = useRef(null);
  
  // Cargar datos al montar o cambiar marketplace
  useEffect(() => {
    // Reset estado al cambiar marketplace
    setCacheKey(null);
    setBaseCacheKey(null);
    setStats(null);
    setMetricas(null);
    setError(null);
    lastConfigRef.current = {
      horizonteTemporal: 'Mensual',
      tipoVolumen: 'Predicciones',
      fuentePrecio: 'Publications (actual)',
      cacheKey: null
    };
  }, [marketplace]);
  
  // Recargar métricas cuando cambie la configuración
  useEffect(() => {
    if (!cacheKey) return;
    
    // Verificar si la configuración realmente cambió
    const currentConfig = {
      horizonteTemporal,
      tipoVolumen,
      fuentePrecio,
      cacheKey
    };
    
    const lastConfig = lastConfigRef.current;
    
    // Detectar qué cambió
    const priceSourceChanged = lastConfig.fuentePrecio !== fuentePrecio;
    const otherParamsChanged = 
      lastConfig.horizonteTemporal !== horizonteTemporal ||
      lastConfig.tipoVolumen !== tipoVolumen;
    const cacheKeyChanged = lastConfig.cacheKey !== cacheKey && lastConfig.cacheKey !== null;
    
    // Si el cacheKey cambió debido a una recalculación de precio, no volver a recalcular
    if (cacheKeyChanged && !priceSourceChanged && !otherParamsChanged) {
      // Solo recargar métricas con el nuevo cacheKey
      loadMetrics();
      lastConfigRef.current = currentConfig;
      return;
    }
    
    // Si nada cambió realmente, no hacer nada
    if (!priceSourceChanged && !otherParamsChanged && !cacheKeyChanged) {
      return;
    }
    
    // Actualizar referencia antes de procesar
    lastConfigRef.current = currentConfig;
    
    // Si la fuente de precio cambió, recalcular
    if (priceSourceChanged) {
      recalculateWithPriceSource();
    } else {
      // Solo recargar métricas si otros parámetros cambiaron
      loadMetrics();
    }
  }, [horizonteTemporal, tipoVolumen, cacheKey, fuentePrecio]);
  
  const loadData = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    // Cancelar peticiones pendientes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    try {
      const result = await financialService.loadFinancialData(marketplace, forceRefresh);
      
      if (result.success) {
        setCacheKey(result.cache_key);
        setBaseCacheKey(result.cache_key); // Guardar cache key base
        setStats(result.stats);
        
        // Reset fuente de precio a actual cuando se cargan datos nuevos
        setFuentePrecio('Publications (actual)');
        
        // Cargar métricas automáticamente
        await loadMetrics(result.cache_key);
      } else {
        setError('Error al cargar datos financieros');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Petición cancelada');
        return;
      }
      
      console.error('Error cargando datos financieros:', err);
      const message = err.response?.data?.detail || err.message || 'Error desconocido';
      setError(`Error al cargar datos: ${message}`);
      
      // Mostrar mensaje específico según el error
      if (err.response?.status === 404) {
        alert('No se encontraron datos. Verifique que existan datos de equivalencias y Newton para este marketplace.');
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };
  
  const loadMetrics = async (key = null) => {
    const keyToUse = key || cacheKey;
    if (!keyToUse) return;
    
    try {
      const result = await financialService.getDashboard(
        marketplace,
        keyToUse,
        horizonteTemporal,
        tipoVolumen
      );
      
      if (result.success) {
        setMetricas(result.metricas_principales);
        setLastUpdate(new Date().toISOString());
      }
    } catch (err) {
      console.error('Error cargando métricas:', err);
      // No mostrar error al usuario, las métricas se recargan automáticamente
    }
  };

  const recalculateWithPriceSource = async () => {
    if (!baseCacheKey && !cacheKey) return;
    
    // Prevenir recalculaciones concurrentes
    if (recalculating) {
      console.log('Recalculación ya en progreso, ignorando...');
      return;
    }
    
    setRecalculating(true);
    setError(null);
    
    try {
      // Usar baseCacheKey si existe, sino usar cacheKey actual
      const keyToRecalculate = baseCacheKey || cacheKey;
      
      console.log(`Recalculando con fuente: ${fuentePrecio}, cache_key: ${keyToRecalculate}`);
      
      const result = await financialService.recalculateWithPriceSource(
        marketplace,
        keyToRecalculate,
        fuentePrecio
      );
      
      if (result.success) {
        // Mostrar estadísticas de aplicación de precios
        if (result.stats) {
          console.log(`Fuente de precio: ${fuentePrecio}`);
          console.log(`Precios aplicados: ${result.stats.precio_aplicado_count}/${result.stats.precio_total_count} productos`);
          
          // Log de diagnóstico detallado
          if (result.stats.diferencia_pct_promedio !== undefined) {
            console.log(`Diagnóstico de precios:`);
            console.log(`  - Precio original promedio: $${result.stats.precio_original_promedio?.toFixed(2)}`);
            console.log(`  - Precio histórico promedio: $${result.stats.precio_historico_promedio?.toFixed(2)}`);
            console.log(`  - Diferencia: ${result.stats.diferencia_pct_promedio?.toFixed(2)}%`);
            console.log(`  - Productos con precio mayor/menor/igual: ${result.stats.productos_precio_mayor}/${result.stats.productos_precio_menor}/${result.stats.productos_precio_igual}`);
          }
          
          // Mostrar notificación al usuario
          if (result.stats.message) {
            // Crear notificación visual mejorada
            const notification = document.createElement('div');
            notification.className = 'price-recalc-notification';
            
            let notificationHTML = `<strong>✓ Precios Actualizados</strong><br>${result.stats.message}`;
            
            // Agregar información adicional si está disponible
            if (result.stats.diferencia_pct_promedio !== undefined) {
              const diffIcon = result.stats.diferencia_pct_promedio > 0 ? '📈' : result.stats.diferencia_pct_promedio < 0 ? '📉' : '➡️';
              notificationHTML += `<br><small>${diffIcon} Cambio: ${result.stats.diferencia_pct_promedio.toFixed(1)}%</small>`;
            }
            
            notification.innerHTML = notificationHTML;
            notification.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #4CAF50; color: white; padding: 15px 20px; border-radius: 5px; z-index: 10000; box-shadow: 0 2px 5px rgba(0,0,0,0.2); max-width: 400px; line-height: 1.5;';
            document.body.appendChild(notification);
            setTimeout(() => notification.remove(), 7000);
          }
        }
        
        // Actualizar cache_key SOLO si cambió
        if (result.new_cache_key && result.new_cache_key !== cacheKey) {
          console.log(`Actualizando cacheKey: ${cacheKey} -> ${result.new_cache_key}`);
          setCacheKey(result.new_cache_key);
        }
        
        // Si volvemos a "Publications (actual)", restaurar baseCacheKey
        if (fuentePrecio === 'Publications (actual)' && baseCacheKey) {
          console.log(`Restaurando baseCacheKey: ${baseCacheKey}`);
          setCacheKey(baseCacheKey);
        }
        
        // Cargar métricas con el cache_key correcto
        const keyToUse = result.new_cache_key || cacheKey;
        await loadMetrics(keyToUse);
      }
    } catch (err) {
      console.error('Error recalculando con fuente de precio:', err);
      const message = err.response?.data?.detail || err.message || 'Error desconocido';
      setError(`Error al recalcular con fuente de precio: ${message}`);
      // No hacer fallback para mantener el estado consistente
    } finally {
      setRecalculating(false);
    }
  };
  
  const handleSaveToDb = async () => {
    if (!cacheKey) {
      alert('Debe cargar datos primero');
      return;
    }
    
    if (!window.confirm('¿Desea guardar los datos financieros en la base de datos?')) {
      return;
    }
    
    setLoading(true);
    try {
      const result = await financialService.saveToDatabase(marketplace, cacheKey);
      if (result.success) {
        alert(`✅ Datos guardados exitosamente: ${result.saved_rows} registros`);
      }
    } catch (err) {
      console.error('Error guardando en BD:', err);
      alert('❌ Error al guardar en la base de datos: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleClearCache = async () => {
    if (!window.confirm('¿Está seguro de limpiar el caché? Deberá recargar los datos.')) {
      return;
    }
    
    setLoading(true);
    try {
      const result = await financialService.clearCache(marketplace);
      if (result.success) {
        setCacheKey(null);
        setStats(null);
        setMetricas(null);
        alert(`✅ Caché limpiado: ${result.deleted_count} entradas eliminadas`);
      }
    } catch (err) {
      console.error('Error limpiando caché:', err);
      alert('❌ Error al limpiar caché: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const tabs = [
    { id: 'dimension', label: '🔍 Por Dimensión', show: !!cacheKey },
    { id: 'top', label: '🏆 Top Utilidad', show: !!cacheKey },
    { id: 'distribution', label: '📈 Distribución', show: !!cacheKey },
    { id: 'comparative', label: '📊 Comparativo', show: !!cacheKey },
    { id: 'detailed', label: '📋 Datos Detallados', show: !!cacheKey }
  ];
  
  return (
    <div className="financial-dashboard">
      <div className="dashboard-header">
        <h2>💰 Análisis Financiero - {marketplace}</h2>
        
        <div className="action-buttons">
          <button 
            onClick={() => loadData(false)} 
            disabled={loading}
            className="btn btn-primary"
            title="Cargar datos desde caché o generar si no existen"
          >
            🔄 Cargar Datos
          </button>
          
          <button 
            onClick={() => loadData(true)} 
            disabled={loading}
            className="btn btn-secondary"
            title="Forzar regeneración de datos desde la base de datos"
          >
            🔄 Recalcular
          </button>
          
          {cacheKey && (
            <>
              <button 
                onClick={handleSaveToDb} 
                disabled={loading}
                className="btn btn-success"
                title="Guardar datos financieros en la base de datos"
              >
                💾 Guardar en BD
              </button>
              
              <button 
                onClick={handleClearCache} 
                disabled={loading}
                className="btn btn-danger"
                title="Eliminar datos del caché"
              >
                🗑️ Limpiar Caché
              </button>
            </>
          )}
        </div>
      </div>
      
      {error && (
        <div className="error-banner">
          ❌ {error}
        </div>
      )}
      
      {stats && (
        <div className="stats-info">
          <span className="stat-item">📊 Fuente: <strong>{stats.source}</strong></span>
          <span className="stat-item">📝 Registros: <strong>{stats.total_records.toLocaleString()}</strong></span>
          <span className="stat-item">🏷️ SKUs: <strong>{stats.unique_skus.toLocaleString()}</strong></span>
          {stats.unique_providers > 0 && (
            <span className="stat-item">🏢 Proveedores: <strong>{stats.unique_providers}</strong></span>
          )}
        </div>
      )}
      
      {!cacheKey && !loading && (
        <div className="welcome-message">
          <h3>💡 Bienvenido al Análisis Financiero</h3>
          <p>Haga clic en <strong>"Cargar Datos"</strong> para comenzar el análisis financiero de {marketplace}.</p>
          <p className="info-text">
            Este módulo proporciona análisis detallado de:
          </p>
          <ul className="features-list">
            <li>💰 Utilidad neta y ROI por producto/proveedor</li>
            <li>📊 Volúmenes de venta históricos y predicciones</li>
            <li>📈 Distribuciones y tendencias de rentabilidad</li>
            <li>🔍 Análisis comparativos multidimensionales</li>
          </ul>
        </div>
      )}
      
      {cacheKey && (
        <>
          <FinancialControls
            horizonteTemporal={horizonteTemporal}
            setHorizonteTemporal={setHorizonteTemporal}
            tipoVolumen={tipoVolumen}
            setTipoVolumen={setTipoVolumen}
            fuentePrecio={fuentePrecio}
            setFuentePrecio={setFuentePrecio}
            recalculating={recalculating}
            lastUpdate={lastUpdate}
          />
          

          {cacheKey && (
            <PriceExploration 
              key={cacheKey}
              marketplace={marketplace}
              cacheKey={cacheKey}
              horizonteTemporal={horizonteTemporal}
              tipoVolumen={tipoVolumen}
            />
          )}
          
          <div className="tabs-container">
            <div className="tabs">
              {tabs.filter(tab => tab.show).map(tab => (
                <button 
                  key={tab.id}
                  className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            
            <div className="tab-content">
              {activeTab === 'dimension' && (
                <DimensionAnalysis 
                  key={cacheKey}
                  marketplace={marketplace} 
                  cacheKey={cacheKey}
                  horizonteTemporal={horizonteTemporal}
                  tipoVolumen={tipoVolumen}
                />
              )}
              
              {activeTab === 'top' && (
                <TopUtilityCharts 
                  key={cacheKey}
                  marketplace={marketplace} 
                  cacheKey={cacheKey}
                  horizonteTemporal={horizonteTemporal}
                  tipoVolumen={tipoVolumen}
                />
              )}
              
              {activeTab === 'distribution' && (
                <DistributionCharts 
                  key={cacheKey}
                  marketplace={marketplace} 
                  cacheKey={cacheKey}
                  horizonteTemporal={horizonteTemporal}
                  tipoVolumen={tipoVolumen}
                />
              )}
              
              {activeTab === 'comparative' && (
                <ComparativeCharts 
                  key={cacheKey}
                  marketplace={marketplace} 
                  cacheKey={cacheKey}
                  horizonteTemporal={horizonteTemporal}
                  tipoVolumen={tipoVolumen}
                />
              )}
              
              {activeTab === 'detailed' && (
                <DetailedDataTable 
                  key={cacheKey}
                  marketplace={marketplace} 
                  cacheKey={cacheKey}
                  fuentePrecio={fuentePrecio}
                  horizonteTemporal={horizonteTemporal}
                  tipoVolumen={tipoVolumen}
                />
              )}
            </div>
          </div>
        </>
      )}
      
      {(loading || recalculating) && (
        <div className="loading-overlay">
          <div className="spinner-container">
            <div className="spinner"></div>
            <p>
              {recalculating 
                ? `Recalculando con ${fuentePrecio}...` 
                : 'Cargando datos financieros...'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialDashboard;

